<template>
  <!--
    P3 (student-foundation-redesign) -- one reusable presentational card for
    Home's Teacher / Advisor / Reports entries. `variant="primary"` (default)
    matches the rendered prototype's `.human-path-card` geometry directly
    (00_*.md §12 PF-09; icon-top row, copy below, a divided action row) for
    the Teacher/Advisor primary row. `variant="compact"` is the approved P3
    subordinate treatment for Reports (PF-11.5) -- required on Home by human
    decision, but never occupying the prototype's Teacher/Advisor slot, so it
    stays deliberately smaller/secondary rather than matching that geometry.
    Purely presentational: no fetching, no store access -- the parent
    (StudentHomeRich.vue) supplies real data and the destination.
  -->
  <!--
    SX-02: without a `to` the card is a real <button> that only emits `click`
    -- the shape the gated Teacher card needs (badge + shared Coming Soon
    dialog, no navigation). Same classes, same geometry either way.
  -->
  <component
    :is="to ? 'router-link' : 'button'"
    v-bind="to ? { to } : { type: 'button' }"
    class="home-action-card"
    :class="[
      `home-action-card--${variant}`,
      { 'home-action-card--button': !to },
    ]"
    :data-testid="testid"
    v-on="to ? {} : { click: onButtonClick }"
  >
    <span class="home-action-card__icon" aria-hidden="true">
      <student-home-icon :name="icon" :size="iconSize" />
    </span>
    <span class="home-action-card__copy">
      <span class="home-action-card__title-row">
        <strong class="home-action-card__title">{{ title }}</strong>
        <span
          v-if="comingSoon"
          class="home-action-card__badge"
          data-testid="home-action-card-coming-soon"
          >{{ $t('studentHome.comingSoonBadge') }}</span
        >
      </span>
      <small class="home-action-card__sub">{{ sub }}</small>
    </span>
    <span class="home-action-card__action">
      {{ actionText }}
      <student-home-icon name="arrow" :size="16" />
    </span>
  </component>
</template>

<script>
import StudentHomeIcon from './StudentHomeIcon.vue'

export default {
  name: 'HomeActionCard',
  components: { StudentHomeIcon },
  props: {
    // Optional since SX-02: absent = non-navigating button (emits `click`).
    to: { type: [String, Object], default: null },
    icon: { type: String, required: true },
    title: { type: String, required: true },
    sub: { type: String, required: true },
    actionText: { type: String, required: true },
    comingSoon: { type: Boolean, default: false },
    testid: { type: String, default: null },
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'compact'].includes(v),
    },
  },
  computed: {
    iconSize() {
      return this.variant === 'compact' ? 22 : 24
    },
  },
  methods: {
    onButtonClick() {
      this.$emit('click')
    },
  },
}
</script>

<style lang="scss" scoped>
// Values on the `--primary` variant are measured directly from the rendered
// prototype's `.human-path-card` (00_*.md §12 PF-09), not from its source.
$home-cyan-bg: #e9f9fd;
$home-cyan: #087d9e;
$home-border: #e2e8f0;
$home-muted: #65718d;
$home-text: #0b163f;
$home-amber-50: #fff7ed;
$home-amber-700: #9a5b13;

.home-action-card {
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus-visible {
    text-decoration: none;
  }
}

// SX-02: the button form must be geometrically identical to the link form
// (Home is a measured parity surface), so only the UA button defaults that
// the card rules do not already override are reset here.
.home-action-card--button {
  appearance: none;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  font: inherit;
  // `inherit`, not `start`: the link form inherits the page's RTL `right`,
  // and the rendered comparison must show the two forms as identical.
  text-align: inherit;
  cursor: pointer;
}

// --- primary: matches the prototype's human-path-card geometry directly ---
.home-action-card--primary {
  display: grid;
  // Prototype measurement: the icon box sits at the card's inline-start
  // (rightmost under RTL) with the copy block filling the rest -- so icon
  // is the FIRST grid column here, not the last.
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'icon copy'
    'action action';
  align-items: start;
  gap: 12px;
  padding: 18px;
  border: 1px solid $home-border;
  border-radius: 21px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(7, 20, 71, 0.055);
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover,
  &:focus-visible {
    border-color: $home-cyan;
  }

  .home-action-card__icon {
    grid-area: icon;
    width: 48px;
    height: 48px;
    border-radius: 15px;
    background: $home-cyan-bg;
    color: $home-cyan;
  }

  .home-action-card__copy {
    grid-area: copy;
  }

  .home-action-card__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.5;
  }

  .home-action-card__sub {
    font-size: 13px;
    line-height: 1.6;
  }

  .home-action-card__action {
    grid-area: action;
    justify-content: flex-start;
    margin-top: 6px;
    padding-top: 12px;
    border-top: 1px solid $home-border;
    font-weight: 700;
    color: $home-cyan;
  }
}

// --- compact: approved P3 subordinate treatment for Reports (PF-11.5) --
// deliberately the simpler, lower-emphasis horizontal layout, never the
// primary card's geometry.
.home-action-card--compact {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid $home-border;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(7, 20, 71, 0.045);
  transition: border-color 0.15s;

  &:hover,
  &:focus-visible {
    border-color: $home-cyan;
  }

  .home-action-card__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: $home-cyan-bg;
    color: $home-cyan;
  }

  .home-action-card__copy {
    flex: 1;
    min-width: 0;
  }

  .home-action-card__title {
    font-size: 14px;
    font-weight: 700;
  }

  .home-action-card__sub {
    font-size: 12px;
  }

  .home-action-card__action {
    flex: 0 0 auto;
    color: #314bd6;
    font-weight: 700;
    white-space: nowrap;
  }
}

.home-action-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-action-card__copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

// P39 TR-035: the row may wrap. On a phone the two-word Advisor title takes
// two lines and the nowrap «به‌زودی» badge no longer fits beside it, so it hung
// off the card; it now drops below the title. The title's line-break size is
// its narrowest form (basis 0, min-content floor) and it grows back only up to
// its own width, so the badge moves only where it cannot fit even beside a
// wrapped title -- every width that already fitted keeps its layout. The
// prototype's Advisor card has no badge, so there is no prototype position to
// keep.
.home-action-card__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.home-action-card__title {
  flex: 1 1 0;
  max-width: max-content;
  color: $home-text;
}

.home-action-card__badge {
  font-size: 11px;
  font-weight: 700;
  color: $home-amber-700;
  background: $home-amber-50;
  border-radius: 999px;
  padding: 2px 9px;
  white-space: nowrap;
}

.home-action-card__sub {
  color: $home-muted;
}

.home-action-card__action {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

// The "arrow" glyph is copied directly from the rendered prototype's own
// RTL output (00_*.md §13.1), so it is right as drawn in Persian.

// P43-02 (Owner UAT): this is a Forward / Continue arrow: «←» in Persian (the
// glyph as drawn), «→» in English.
html:not(.is-rtl) .home-action-card__action ::v-deep svg {
  transform: scaleX(-1);
}
</style>
